import { useCallback, useEffect, useState } from "react";
import { theme, btnPrimary, inputStyle, type as text } from "../theme";
import { StatusTag } from "./Drawing";
import { DetailSection } from "./RecordView";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate } from "../lib/format";
import { secondaryBtn } from "../pages/resilience/shared";

// Control owner attestations (docs/RECORD-CHECKS.md "Control owner attestations", backend
// lib/controlAttestations.ts). On a control's detail: who owns it, how often they attest that it still
// operates as described, where that stands and the history. Naming the owner needs controls:write; the
// attestation itself is the owner's, on My Tasks. It never changes the control's status: a lapsed or
// "not operating" attestation is a failed record check on the control.

type Standing = "awaiting_first" | "current" | "due_soon" | "lapsed" | "not_operating";
interface Ownership { controlId: string; ownerUserId: string | null; ownerName: string | null; ownerActive: boolean; cycleMonths: number; nextDueAt: string; standing: Standing; last: { outcome: string; attestedAt: string; note: string | null; byName: string | null } | null }
interface History { id: string; outcome: string; note: string | null; attestedAt: string; byName: string | null }

const STANDING: Record<Standing, { tone: "met" | "partial" | "gap" | "none"; label: string }> = {
  awaiting_first: { tone: "none", label: "First attestation due" },
  current: { tone: "met", label: "Attested" },
  due_soon: { tone: "partial", label: "Attestation due soon" },
  lapsed: { tone: "gap", label: "Attestation overdue" },
  not_operating: { tone: "gap", label: "Owner says not operating" },
};
const smallBtn: React.CSSProperties = { ...secondaryBtn, padding: "4px 10px", fontSize: 12 };

export function ControlAttestation({ controlId, canEdit }: { controlId: string; canEdit: boolean }) {
  const [view, setView] = useState<{ ownership: Ownership | null; history: History[]; cycles: number[] } | null>(null);
  const [members, setMembers] = useState<Array<{ id: string; name: string }>>([]);
  const [editing, setEditing] = useState(false);
  const [ownerId, setOwnerId] = useState("");
  const [cycle, setCycle] = useState(6);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setView(await api.get(`/control-attestations/control/${encodeURIComponent(controlId)}`)); } catch { setView(null); }
  }, [controlId]);
  useEffect(() => { void load(); }, [load]);

  async function startEdit() {
    setEditing(true); setError(null);
    setOwnerId(view?.ownership?.ownerUserId ?? ""); setCycle(view?.ownership?.cycleMonths ?? 6);
    if (!members.length) {
      try {
        const d = await api.get<{ members: Array<{ id: string; displayName: string | null; email: string; isActive?: boolean }> }>("/organizations/members");
        setMembers(d.members.filter((m) => m.isActive !== false).map((m) => ({ id: m.id, name: m.displayName || m.email })));
      } catch { /* the picker stays empty */ }
    }
  }

  async function save(clear = false) {
    setBusy(true); setError(null);
    try {
      await api.put(`/control-attestations/owners/${encodeURIComponent(controlId)}`, { ownerUserId: clear ? null : ownerId || null, cycleMonths: cycle });
      setEditing(false); await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not save the owner.")); }
    finally { setBusy(false); }
  }

  if (!view) return null;
  const o = view.ownership;
  if (!o && !canEdit) return null;
  return (
    <DetailSection title="Owner and attestation" aside={o ? <StatusTag tone={STANDING[o.standing].tone}>{STANDING[o.standing].label}</StatusTag> : undefined}>
      {o ? (
        <div style={text.body}>
          {o.ownerName ?? "No owner"}{o.ownerName && !o.ownerActive ? " (no longer active)" : ""} attests every {o.cycleMonths} months that this control operates as described.
          <div style={{ ...text.hint, marginTop: 4 }}>
            {o.last ? `Last attested ${fmtDate(o.last.attestedAt)}: ${o.last.outcome === "operating" ? "operating" : `not operating — ${o.last.note ?? ""}`}. ` : ""}
            Next due {fmtDate(o.nextDueAt)}. An overdue or "not operating" attestation fails this control's record check; it never changes the status above.
          </div>
        </div>
      ) : (
        <div style={text.hint}>No owner is named. Name one to have them confirm on a cycle that this control still operates as described.</div>
      )}
      {view.history.length > 1 && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ ...text.meta, cursor: "pointer" }}>Earlier attestations ({view.history.length - 1})</summary>
          {view.history.slice(1).map((h) => (
            <div key={h.id} style={{ ...text.hint, marginTop: 4 }}>{fmtDate(h.attestedAt)}{h.byName ? `, ${h.byName}` : ""}: {h.outcome === "operating" ? "operating" : `not operating — ${h.note ?? ""}`}</div>
          ))}
        </details>
      )}
      {error && <div role="alert" style={{ ...text.hint, color: "var(--c-dangerText)", marginTop: 6 }}>{error}</div>}
      {canEdit && !editing && (
        <div style={{ marginTop: 8 }}><button type="button" style={smallBtn} onClick={() => void startEdit()}>{o ? "Change owner" : "Name an owner"}</button></div>
      )}
      {canEdit && editing && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          <select aria-label="Control owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} style={{ ...inputStyle, width: 240 }}>
            <option value="">Choose a member</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select aria-label="Attestation cycle" value={cycle} onChange={(e) => setCycle(Number(e.target.value))} style={{ ...inputStyle, width: 160 }}>
            {view.cycles.map((c) => <option key={c} value={c}>Every {c} months</option>)}
          </select>
          <button type="button" disabled={busy || !ownerId} onClick={() => void save()} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }}>Save</button>
          {o && <button type="button" disabled={busy} onClick={() => void save(true)} style={smallBtn}>Remove owner</button>}
          <button type="button" onClick={() => setEditing(false)} style={{ ...smallBtn, color: theme.color.muted }}>Cancel</button>
        </div>
      )}
    </DetailSection>
  );
}
